
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  FormControlLabel,
  Snackbar,
  Alert,
  Typography,
} from '@mui/material';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Autocomplete, Checkbox } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAutoPaginationNavigation } from '@/utils/autoPaginationNavigation';
import {
  useCreateScheduleMutation,
  useGetActivityTypesDropdownQuery,
  useSearchAssetsQuery,
  useGetUserGroupsDropdownQuery,
  useGetCostTemplatesDropdownQuery,
  useGetChecklistTemplatesDropdownQuery,
} from '../../store/api/scheduleApi';
import {
  AssetSearchResult,
} from '../../types/schedule.types';
import { ScheduleCreateInputWithMovementTracking } from '../../types/movement-tracking.types';
import { ScheduleStatus, getScheduleStatusLabel } from '../../enums/schedule.enums';
import styles from '../AssetManagement/AssetCreate/AssetCreate.module.scss';
import { useDebounce } from '../../hooks/useDebounce';
import AutoCompleteSelect from '@/components/common/AutoCompleteSelect';

interface FrequencyData {
  type: string;
  interval: number;
  days_of_week?: string[];
  days_of_month?: number[];
  months?: string[];
}

const ScheduleCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { toListing } = useAutoPaginationNavigation(navigate, '/schedule-management/schedules');
  const [formData, setFormData] = useState<ScheduleCreateInputWithMovementTracking>({
    name: '',
    description: '',
    activity_type_id: '',
    asset_id: '',
    start_date: '',
    frequency: 1,
    frequency_type: 'daily',
    frequency_days: [],
    frequency_months: [],
    end_date: '',
    user_group_id: '',
    execution_before: 0,
    reminder_before: 0,
    cost_template_id: '',
    checklist_template_id: '',
    attachments: [],
    status: ScheduleStatus.ACTIVE,
    // Movement tracking fields
    movement_tracking_required: false,
    tracking_type: null,
  });

  // Local state for frequency data
  const [frequencyData, setFrequencyData] = useState<FrequencyData>({
    type: 'daily',
    interval: 1,
    days_of_week: [],
    days_of_month: [],
    months: [],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [assetSearchTerm, setAssetSearchTerm] = useState('');
  const [selectedAsset, setSelectedAsset] = useState<AssetSearchResult | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activityTypeState, setActivityTypeState] = useState({
    search: '',
    page: 1,
    data: [] as any[],
    hasMore: true,
  });
  const [userGroupState, setUserGroupState] = useState({
    search: '',
    page: 1,
    data: [] as any[],
    hasMore: true,
  });
  const [costTemplateState, setCostTemplateState] = useState({
    search: '',
    page: 1,
    data: [] as any[],
    hasMore: true,
  });
  const [checklistTemplateState, setChecklistTemplateState] = useState({
    search: '',
    page: 1,
    data: [] as any[],
    hasMore: true,
  });

  const debouncedActivityTypeSearch = useDebounce(activityTypeState.search, 300);
  const debouncedUserGroupSearch = useDebounce(userGroupState.search, 300);
  const debouncedCostTemplateSearch = useDebounce(costTemplateState.search, 300);
  const debouncedChecklistTemplateSearch = useDebounce(checklistTemplateState.search, 300);

  // API hooks
  const {
    data: activityTypeData,
    isLoading: isLoadingActivityTypes,
    isFetching: isActivityTypeFetching
  } = useGetActivityTypesDropdownQuery({ 
    page: activityTypeState.page, 
    dropdown: true,
    limit: 25, 
    search: debouncedActivityTypeSearch || undefined
  });

  const {
    data: userGroupData,
    isLoading: isLoadingUserGroups,
    isFetching: isUserGroupFetching
  } = useGetUserGroupsDropdownQuery({ 
    page: userGroupState.page, 
    dropdown: true,
    limit: 25, 
    search: debouncedUserGroupSearch || undefined
  });

  const {
    data: costTemplateData,
    isLoading: isLoadingCostTemplates,
    isFetching: isCostTemplateFetching
  } = useGetCostTemplatesDropdownQuery({ 
    page: costTemplateState.page, 
    dropdown: true,
    limit: 25, 
    search: debouncedCostTemplateSearch || undefined
  });

  const {
    data: checklistTemplateData,
    isLoading: isLoadingChecklistTemplates,
    isFetching: isChecklistTemplateFetching
  } = useGetChecklistTemplatesDropdownQuery({ 
    page: checklistTemplateState.page, 
    dropdown: true,
    limit: 25, 
    search: debouncedChecklistTemplateSearch || undefined
  });

  const activityTypeOptions = useMemo(() => {
    return activityTypeState.data.map((user: any) => ({
      label: user.name || 'Unknown User',
      value: user.id,
    }));
  }, [activityTypeState.data]);

  const userGroupOptions = useMemo(() => {
    return userGroupState.data.map((user: any) => ({
      label: user.name || 'Unknown User',
      value: user.id,
    }));
  }, [userGroupState.data]);

  const costTemplateOptions = useMemo(() => {
    return costTemplateState.data.map((item: any) => ({
      label: item.name || 'Unknown',
      value: item.id,
    }));
  }, [costTemplateState.data]);

  const checklistTemplateOptions = useMemo(() => {
    return checklistTemplateState.data.map((item: any) => ({
      label: item.name || 'Unknown',
      value: item.id,
    }));
  }, [checklistTemplateState.data]);

  useEffect(() => {
    if (!activityTypeData) return;

    setActivityTypeState((prev) => {
      const merged =
        prev.page === 1
          ? [...activityTypeData]
          : [...prev.data, ...activityTypeData];

      const unique = merged.filter(
        (item, index, self) =>
          index === self.findIndex((i) => i.id === item.id)
      );

      return {
        ...prev,
        data: unique,
        hasMore: activityTypeData.length === 25, // since limit = 25
      };
    });
  }, [activityTypeData]);

  useEffect(() => {
    if (!userGroupData?.results) return;

    setUserGroupState((prev) => {
      const merged =
        prev.page === 1
          ? [...userGroupData.results]
          : [...prev.data, ...userGroupData.results];

      const unique = merged.filter(
        (item, index, self) =>
          index === self.findIndex((i) => i.id === item.id)
      );

      return {
        ...prev,
        data: unique,
        hasMore: !!userGroupData.next,
      };
    });
  }, [userGroupData]);

  useEffect(() => {
    if (!costTemplateData?.results) return;

    setCostTemplateState((prev) => {
      const merged =
        prev.page === 1
          ? [...costTemplateData.results]
          : [...prev.data, ...costTemplateData.results];

      const unique = merged.filter(
        (item, index, self) =>
          index === self.findIndex((i) => i.id === item.id)
      );

      return {
        ...prev,
        data: unique,
        hasMore: !!costTemplateData.next,
      };
    });
  }, [costTemplateData]);

  useEffect(() => {
    if (!checklistTemplateData?.results) return;

    setChecklistTemplateState((prev) => {
      const merged =
        prev.page === 1
          ? [...checklistTemplateData.results]
          : [...prev.data, ...checklistTemplateData.results];

      const unique = merged.filter(
        (item, index, self) =>
          index === self.findIndex((i) => i.id === item.id)
      );

      return {
        ...prev,
        data: unique,
        hasMore: !!checklistTemplateData.next,
      };
    });
  }, [checklistTemplateData]);
  
  const handleActivityTypeSearch = (value: string) => {
    setActivityTypeState({
      search: value,
      page: 1,
      data: [],
      hasMore: true,
    });
  };

  const handleUserGroupSearch = (value: string) => {
    setUserGroupState({
      search: value,
      page: 1,
      data: [],
      hasMore: true,
    });
  };

  const handleCostTemplateSearch = (value: string) => {
    setCostTemplateState({
      search: value,
      page: 1,
      data: [],
      hasMore: true,
    });
  };

  const handleChecklistTemplateSearch = (value: string) => {
    setChecklistTemplateState({
      search: value,
      page: 1,
      data: [],
      hasMore: true,
    });
  };

  const handleActivityTypeScrollEnd = () => {
    if (activityTypeState.hasMore && !isLoadingActivityTypes && !isActivityTypeFetching) {
      setActivityTypeState(prev => ({
        ...prev,
        page: prev.page + 1
      }));
    }
  };

  const handleUserGroupScrollEnd = () => {
    if (userGroupState.hasMore && !isLoadingUserGroups && !isUserGroupFetching) {
      setUserGroupState(prev => ({
        ...prev,
        page: prev.page + 1
      }));
    }
  };

  const handleCostTemplateScrollEnd = () => {
    if (costTemplateState.hasMore && !isLoadingCostTemplates && !isCostTemplateFetching) {
      setCostTemplateState(prev => ({
        ...prev,
        page: prev.page + 1
      }));
    }
  };

  const handleChecklistTemplateScrollEnd = () => {
    if (checklistTemplateState.hasMore && !isLoadingChecklistTemplates && !isChecklistTemplateFetching) {
      setChecklistTemplateState(prev => ({
        ...prev,
        page: prev.page + 1
      }));
    }
  };

  const { data: assetSearchResults, isLoading: isSearchingAssets } =
    useSearchAssetsQuery(
      { query: assetSearchTerm, dropdown: true },
      { skip: assetSearchTerm.length < 2 }
    );
  
  const [createSchedule, { isLoading: isSubmitting }] =
    useCreateScheduleMutation();

  // Handle form input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    if (name) {
      setFormData((prev: ScheduleCreateInputWithMovementTracking) => ({ ...prev, [name]: value }));

      // Clear error when field is updated
      if (errors[name]) {
        setErrors((prev: Record<string, string>) => ({ ...prev, [name]: '' }));
      }
    }
  };

  // Handle asset selection
  const handleAssetSelect = (asset: AssetSearchResult | null) => {
    setSelectedAsset(asset);
    if (asset) {
      setFormData((prev: ScheduleCreateInputWithMovementTracking) => ({ ...prev, asset_id: asset.id }));

      // Access location information from location_tree field based on API response structure
      const locationTree = (asset as any).location_detail;
      if (locationTree) {
        setSelectedLocation({
          id: locationTree.id,
          name: locationTree.name
        });
      }

      // Clear error when asset is selected
      if (errors.asset_id) {
        setErrors((prev: Record<string, string>) => ({ ...prev, asset_id: '' }));
      }
    } else {
      setFormData((prev: ScheduleCreateInputWithMovementTracking) => ({ ...prev, asset_id: '' }));
      setSelectedLocation(null);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    const today = new Date().toISOString().split('T')[0];

    if (!formData.name.trim()) {
      newErrors.name = 'Schedule name is required';
    }

    if (!formData.activity_type_id) {
      newErrors.activity_type_id = 'Activity type is required';
    }

    if (!formData.asset_id) {
      newErrors.asset_id = 'Asset is required';
    }

    // Date validations - Start Date and End Date are mandatory
    if (!formData.start_date || !formData.start_date.trim()) {
      newErrors.start_date = 'Start date is required';
    } else if (formData.start_date < today) {
      newErrors.start_date = 'Start date cannot be in the past';
    }
    
    if (!formData.end_date || !formData.end_date.trim()) {
      newErrors.end_date = 'End date is required';
    } else {
      if (formData.end_date < today) {
        newErrors.end_date = 'End date cannot be in the past';
      }
      
      if (formData.start_date && formData.end_date <= formData.start_date) {
        newErrors.end_date = 'End date must be after start date';
      }
    }

    // Movement tracking validation
    if (formData.movement_tracking_required) {
      if (!formData.tracking_type) {
        newErrors.tracking_type = 'Tracking type is required when movement tracking is enabled';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setUploadedFiles((prev: File[]) => [...prev, ...newFiles]);
      setFormData((prev: ScheduleCreateInputWithMovementTracking) => ({
        ...prev,
        attachments: [...(prev.attachments || []), ...newFiles],
      }));
    }
  };

  // Handle file removal
  const handleFileRemove = (index: number) => {
    setUploadedFiles((prev: File[]) => prev.filter((_: File, i: number) => i !== index));
    setFormData((prev: ScheduleCreateInputWithMovementTracking) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((_: File, i: number) => i !== index),
    }));
  };

  // Trigger file input click
  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle frequency type change
  const handleFrequencyTypeChange = (type: string) => {
    setFrequencyData((prev: FrequencyData) => ({
      ...prev,
      type,
      days_of_week: type === 'weekly' ? ['monday'] : [],
      days_of_month: type === 'monthly' ? [1] : [],
      months: type === 'yearly' ? ['january'] : [],
    }));

    setFormData((prev: ScheduleCreateInputWithMovementTracking) => ({
      ...prev,
      frequency_type: type,
      frequency_days: type === 'weekly' ? ['monday'] : [],
      frequency_months: type === 'yearly' ? ['january'] : [],
    }));
  };

  // Handle frequency interval change
  const handleFrequencyIntervalChange = (interval: number) => {
    setFrequencyData((prev: FrequencyData) => ({
      ...prev,
      interval,
    }));

    setFormData((prev: ScheduleCreateInputWithMovementTracking) => ({
      ...prev,
      frequency: interval,
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, type, checked } = e.target;
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Always use FormData for consistency
      const formDataPayload = new FormData();

      // Add basic fields with proper backend field names
      formDataPayload.append('name', formData.name);
      formDataPayload.append('description', formData.description);
      
      // Convert ID fields to match backend expectations - ensure they're not empty
      if (formData.activity_type_id && formData.activity_type_id.trim() !== '') {
        formDataPayload.append('activity_type', formData.activity_type_id);
      }
      if (formData.asset_id && formData.asset_id.trim() !== '') {
        formDataPayload.append('asset', formData.asset_id);
      }
      if (formData.user_group_id && formData.user_group_id.trim() !== '') {
        formDataPayload.append('user_group', formData.user_group_id);
      }
      if (formData.cost_template_id && formData.cost_template_id.trim() !== '') {
        formDataPayload.append('cost_template', formData.cost_template_id);
      }
      if (formData.checklist_template_id && formData.checklist_template_id.trim() !== '') {
        formDataPayload.append('checklist_template', formData.checklist_template_id);
      }

      // Add date fields - ensure they're not empty
      if (formData.start_date && formData.start_date.trim() !== '') {
        formDataPayload.append('start_date', formData.start_date);
      }
      if (formData.end_date && formData.end_date.trim() !== '') {
        formDataPayload.append('end_date', formData.end_date);
      }

      // Add numeric fields
      formDataPayload.append('frequency', formData.frequency.toString());
      formDataPayload.append('execution_before', formData.execution_before.toString());
      formDataPayload.append('reminder_before', formData.reminder_before.toString());

      // Add boolean fields
      formDataPayload.append('status', formData.status);
      formDataPayload.append('movement_tracking_required', formData.movement_tracking_required.toString());

      // Add tracking type only if movement tracking is required and tracking type is set
      if (formData.movement_tracking_required && formData.tracking_type && formData.tracking_type !== 'null') {
        formDataPayload.append('tracking_type', formData.tracking_type);
      }

      // Add frequency fields if present
      if (formData.frequency_type && formData.frequency_type.trim() !== '') {
        formDataPayload.append('frequency_type', formData.frequency_type);
      }
      if (formData.frequency_days && formData.frequency_days.length > 0) {
        formData.frequency_days.forEach((day: string) => {
          formDataPayload.append('frequency_days', day);
        });
      }
      if (formData.frequency_months && formData.frequency_months.length > 0) {
        formData.frequency_months.forEach((month: string) => {
          formDataPayload.append('frequency_months', month);
        });
      }

      // Add file attachments
      if (formData.attachments && formData.attachments.length > 0) {
        formData.attachments.forEach((file: File) => {
          formDataPayload.append(`documents`, file);
        });
      }

      await createSchedule(formDataPayload).unwrap();
      toListing();
    } catch (error) {
      console.error('Failed to create schedule:', error);
      // Handle API errors
      if (error && typeof error === 'object' && 'data' in error) {
        const apiErrors = error.data as Record<string, string[]>;
        const formattedErrors: Record<string, string> = {};

        Object.entries(apiErrors).forEach(([key, messages]) => {
          formattedErrors[key] = Array.isArray(messages)
            ? messages[0]
            : (messages as string);
        });

        setErrors(formattedErrors);
      }
      setErrorMessage('Failed to create schedule');
      setShowError(true);
    }
  };

  const handleCancel = () => {
    toListing();
  };

  return (
    <div className={`appWrapper ${styles.addAssetContainer}`}>
      <h3 className="pageTitle">Create New Schedule</h3>

      <div className={styles.singlePageForm}>
        <div className={styles.formContent}>
          
          {/* Basic Information Section */}
          <div className={`${styles.AssetStepsContainer} bordered`}>
            <h4 className={styles.pageTitle}>Basic Information</h4>
            <div className={styles.formContainer}>
              
              {/* Schedule Name */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Schedule Name*
                </Typography>
                <TextField
                  fullWidth
                  name="name"
                  variant="outlined"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  placeholder="Schedule Name"
                />
              </div>

              {/* Activity Type */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Activity Type*
                </Typography>
                <FormControl fullWidth error={!!errors.activity_type_id}>
                  <AutoCompleteSelect
                    fullwidth
                    options={activityTypeOptions}
                    loading={isLoadingActivityTypes || isActivityTypeFetching}
                    placeholder="Select Activity type"
                    searchable
                    searchValue={activityTypeState.search}
                    onSearchChange={handleActivityTypeSearch}
                    value={formData.activity_type_id || ''}
                    onChange={(value) =>
                      setFormData(prev => ({
                        ...prev,
                        activity_type_id: value as string
                      }))
                    }
                    onScrollEnd={handleActivityTypeScrollEnd}
                  />
                  {errors.activity_type_id && (
                    <FormHelperText>
                      {errors.activity_type_id}
                    </FormHelperText>
                  )}
                </FormControl>
              </div>

              {/* Asset Selection */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Asset*
                </Typography>
                <Autocomplete
                  placeholder="Asset"
                  options={
                    assetSearchTerm.length >= 2
                      ? assetSearchResults?.results || []
                      : []
                  }
                  getOptionLabel={(option: AssetSearchResult) =>
                    `${option.asset_name} (${option.asset_tag})`
                  }
                  loading={isSearchingAssets}
                  value={selectedAsset}
                  onChange={(_: any, newValue: AssetSearchResult | null) => handleAssetSelect(newValue)}
                  onInputChange={(_: any, newInputValue: string) => {
                    setAssetSearchTerm(newInputValue);
                  }}
                  filterOptions={(x: any) => x}
                  noOptionsText={
                    assetSearchTerm.length < 2
                      ? 'Type at least 2 characters to search'
                      : 'No assets found'
                  }
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      error={!!errors.asset_id}
                      helperText={
                        errors.asset_id || 'Search by asset name or tag'
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isSearchingAssets ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </div>

              {/* Location (Read-only) */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Location
                </Typography>
                <TextField
                  fullWidth
                  value={selectedLocation?.name || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                  disabled
                  helperText="Auto-populated based on selected asset"
                  placeholder="Location"
                />
              </div>

              {/* Status Selection */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Status
                </Typography>
                <FormControl fullWidth>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    displayEmpty
                    placeholder="status"
                  >
                    <MenuItem value={ScheduleStatus.INACTIVE}>
                      {getScheduleStatusLabel(ScheduleStatus.INACTIVE)}
                    </MenuItem>
                    <MenuItem value={ScheduleStatus.ACTIVE}>
                      {getScheduleStatusLabel(ScheduleStatus.ACTIVE)}
                    </MenuItem>
                    <MenuItem value={ScheduleStatus.COMPLETED}>
                      {getScheduleStatusLabel(ScheduleStatus.COMPLETED)}
                    </MenuItem>
                    <MenuItem value={ScheduleStatus.CANCELLED}>
                      {getScheduleStatusLabel(ScheduleStatus.CANCELLED)}
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>

          {/* Execution Period Section */}
          <div className={`${styles.AssetStepsContainer} bordered`}>
            <h4 className={styles.pageTitle}>Execution Period</h4>
            <div className={styles.formContainer}>
              {/* Start Date */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Start Date*
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={formData.start_date ? new Date(formData.start_date) : null}
                    onChange={(date) => {
                      const value = date && !isNaN(date.getTime())
                        ? date.toISOString().split('T')[0]
                        : '';
                      setFormData((prev: ScheduleCreateInputWithMovementTracking) => ({ ...prev, start_date: value }));
                      if (errors.start_date) {
                        setErrors((prev) => ({ ...prev, start_date: '' }));
                      }
                    }}
                    minDate={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.start_date,
                        helperText: errors.start_date,
                        size: 'small',
                      },
                      actionBar: {
                        actions: ['clear'],
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>

              {/* End Date */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  End Date*
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={formData.end_date ? new Date(formData.end_date) : null}
                    onChange= {(date) => {
                      const value = date && !isNaN(date.getTime())
                        ? date.toISOString().split('T')[0]
                        : '';
                      setFormData((prev: ScheduleCreateInputWithMovementTracking) => ({ ...prev, end_date: value }));
                      if (errors.end_date) {
                        setErrors((prev) => ({ ...prev, end_date: '' }));
                      }
                    }}
                    minDate={
                      formData.start_date
                        ? new Date(formData.start_date)
                        : new Date()
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.end_date,
                        helperText: errors.end_date,
                        size: 'small',
                      },
                      actionBar: {
                          actions: ['clear'],
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>

              {/* Frequency Type */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Frequency Type
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={frequencyData.type}
                    onChange={(e) => handleFrequencyTypeChange(e.target.value as string)}
                    displayEmpty
                    placeholder="Frequency Type"
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                  </Select>
                </FormControl>
              </div>

              {/* Frequency Interval */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Repeat Every
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={frequencyData.interval}
                  onChange={(e) =>
                    handleFrequencyIntervalChange(parseInt(e.target.value, 10))
                  }
                  InputProps={{
                    endAdornment: (
                      <Box sx={{ minWidth: 80, textAlign: 'center' }}>
                        {frequencyData.type === 'daily' && 'Days'}
                        {frequencyData.type === 'weekly' && 'Weeks'}
                        {frequencyData.type === 'monthly' && 'Months'}
                        {frequencyData.type === 'yearly' && 'Years'}
                      </Box>
                    ),
                  }}
                  inputProps={{ min: 1 }}
                  error={!!errors.frequency}
                  helperText={errors.frequency}
                  placeholder="Repeat Every"
                />
              </div>
            </div>
          </div>

          {/* Notifications & Assignment Section */}
          <div className={`${styles.AssetStepsContainer} bordered`}>
            <h4 className={styles.pageTitle}>Notifications & Assignment</h4>
            <div className={styles.formContainer}>
              {/* Execution Before */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Execution Before (days)
                </Typography>
                <TextField
                  fullWidth
                  name="execution_before"
                  type="number"
                  value={formData.execution_before}
                  onChange={handleChange}
                  error={!!errors.execution_before}
                  helperText="Days before due date to start execution"
                  inputProps={{ min: 0 }}
                  placeholder="Execution Before"
                />
              </div>

              {/* Reminder Before */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Reminder Before (days)
                </Typography>
                <TextField
                  fullWidth
                  name="reminder_before"
                  type="number"
                  value={formData.reminder_before}
                  onChange={handleChange}
                  error={!!errors.reminder_before}
                  helperText="Days before due date to send reminder"
                  inputProps={{ min: 0 }}
                  placeholder=" Reminder Before"
                />
              </div>

              {/* User Group */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  User Group
                </Typography>
                <FormControl fullWidth error={!!errors.user_group_id}>
                  <AutoCompleteSelect
                    fullwidth
                    options={userGroupOptions}
                    loading={isLoadingUserGroups || isUserGroupFetching}
                    placeholder="Select User Group"
                    searchable
                    searchValue={userGroupState.search}
                    onSearchChange={handleUserGroupSearch}
                    value={formData.user_group_id || ''}
                    onChange={(value) =>
                      setFormData(prev => ({
                        ...prev,
                        user_group_id: value as string
                      }))
                    }
                    onScrollEnd={handleUserGroupScrollEnd}
                  />
                </FormControl>
              </div>
            </div>
          </div>

          {/* Movement Tracking Section */}
          <div className={`${styles.AssetStepsContainer} bordered`}>
            <h4 className={styles.pageTitle}>Movement Tracking</h4>
            <div className={styles.formContainer}>
              {/* Movement Tracking Required */}
              <div className={`${styles.formFields} ${styles.fullWidth}`}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.movement_tracking_required}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFormData((prev: ScheduleCreateInputWithMovementTracking) => ({
                          ...prev,
                          movement_tracking_required: e.target.checked,
                          // Reset tracking type and vehicle if unchecked
                          tracking_type: e.target.checked ? prev.tracking_type : null,
                          vehicle_id: e.target.checked ? prev.vehicle_id : null,
                        }));
                      }}
                      name="movement_tracking_required"
                    />
                  }
                  label="Asset Movement Tracking Required"
                />
              </div>

              {/* Tracking Type */}
              {formData.movement_tracking_required && (
                <div className={`${styles.formFields} ${styles.fullWidth}`}>
                  <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                    Tracking Type*
                  </Typography>
                  <FormControl fullWidth error={!!errors.tracking_type}>
                    <Select
                      value={formData.tracking_type || ''}
                      onChange={(e) => {
                        const trackingType = e.target.value as 'vehicle' | 'asset';
                        setFormData((prev: ScheduleCreateInputWithMovementTracking) => ({
                          ...prev,
                          tracking_type: trackingType,
                          // Reset vehicle_id if switching to asset tracking
                          vehicle_id: trackingType === 'asset' ? null : prev.vehicle_id,
                        }));

                        // Clear error when tracking type is selected
                        if (errors.tracking_type) {
                          setErrors((prev: Record<string, string>) => ({ ...prev, tracking_type: '' }));
                        }
                      }}
                      displayEmpty
                      placeholder="Tracking Type"
                    >
                      <MenuItem value="">Select Tracking Type</MenuItem>
                      <MenuItem value="asset">Asset Tracking</MenuItem>
                      <MenuItem value="vehicle">Vehicle Tracking</MenuItem>
                    </Select>
                    {errors.tracking_type && (
                      <FormHelperText>{errors.tracking_type}</FormHelperText>
                    )}
                  </FormControl>
                </div>
              )}
            </div>
          </div>

          {/* Templates Section */}
          <div className={`${styles.AssetStepsContainer} bordered`}>
            <h4 className={styles.pageTitle}>Templates</h4>
            <div className={styles.formContainer}>
              
              {/* Cost Templates */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Cost Template
                </Typography>
                <FormControl fullWidth error={!!errors.cost_template_id}>
                  <AutoCompleteSelect
                    fullwidth
                    options={costTemplateOptions}
                    loading={isLoadingCostTemplates || isCostTemplateFetching}
                    placeholder="Select Cost Template"
                    searchable
                    searchValue={costTemplateState.search}
                    onSearchChange={handleCostTemplateSearch}
                    value={formData.cost_template_id || ''}
                    onChange={(value) =>
                      setFormData(prev => ({
                        ...prev,
                        cost_template_id: value as string
                      }))
                    }
                    onScrollEnd={handleCostTemplateScrollEnd}
                  />
                </FormControl>
              </div>

              {/* Checklist Templates */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Checklist Template
                </Typography>
                 <FormControl fullWidth error={!!errors.checklist_template_id}>
                  <AutoCompleteSelect
                    fullwidth
                    options={checklistTemplateOptions}
                    loading={isLoadingChecklistTemplates || isChecklistTemplateFetching}
                    placeholder="Select Checklist Template"
                    searchable
                    searchValue={checklistTemplateState.search}
                    onSearchChange={handleChecklistTemplateSearch}
                    value={formData.checklist_template_id || ''}
                    onChange={(value) =>
                      setFormData(prev => ({
                        ...prev,
                        checklist_template_id: value as string
                      }))
                    }
                    onScrollEnd={handleChecklistTemplateScrollEnd}
                  />
                </FormControl>
              </div>
            </div>
          </div>

          {/* Description Section */}
          <div className={`${styles.AssetStepsContainer} bordered`}>
            <h4 className={styles.pageTitle}>Description</h4>
            <div className={styles.formContainer}>
              {/* Description - Updated to 2 rows for better UI - Moved before Attachments */}
              <div className={`${styles.formFields} ${styles.fullWidth}`}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Description
                </Typography>
                <TextField
                  fullWidth
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  placeholder="Enter schedule description..."
                />
              </div>
            </div>
          </div>

          {/* File Attachments Section */}
          <div className={`${styles.AssetStepsContainer} bordered`}>
            <h4 className={styles.pageTitle}>Attachments</h4>
            <div className={styles.formContainer}>
              
              <div className={`${styles.formFields} ${styles.fullWidth}`}>
                <input
                  type="file"
                  multiple
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />

                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleFileButtonClick}
                  sx={{ mb: 2 }}
                >
                  Upload Files
                </Button>

                {uploadedFiles.length > 0 && (
                  <List>
                    {uploadedFiles.map((file: File, index: number) => [
                      <ListItem key={index}>
                        <ListItemText
                          primary={file.name}
                          secondary={`${(file.size / 1024).toFixed(2)} KB`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleFileRemove(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>,
                      ...(index < uploadedFiles.length - 1 ? [<Divider key={`divider-${index}`} />] : [])
                    ])}
                  </List>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className={styles.stickyFooter}  style={{ marginTop: '10px' }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="button button__solid--primary"
          >
            {isSubmitting ? <CircularProgress size={24}/> : "Create Schedule"}
          </Button>
        </div>
      </div>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ScheduleCreatePage;
